import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "@/components/ui/spinner";

// Icons
import { LogIn } from "lucide-react";

// Constants
import { AUTH_KEY } from "@/components/constants";
import { postlogin } from "@/services/login";
import { version } from "../../package.json";

export const Route = createFileRoute("/")({
  component: LoginComponent,
  // beforeLoad: () => {
  // 	const navigate = useNavigate();

  // 	const savedToken = localStorage.getItem(AUTH_KEY);
  // 	if (savedToken) {
  // 		navigate({
  // 			to: "/report/overview",
  // 			search: { active: "riskAssessment" },
  // 		});
  // 	}
  // }
});

function LoginComponent() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    userCode: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useMutation({
    mutationFn: (data: typeof formData) => postlogin(data),
    onSuccess: (data) => {
      console.log(data, "Login Success", data);

      localStorage.setItem(
        AUTH_KEY,
        JSON.stringify({
          accessToken: data.accessToken,
          data: data.data,
        })
      );

      toast({
        title: "ເຂົ້າສູ່ລະບົບສຳເລັດ",
        description: "ສະບາຍດີທ່ານ, ຍິນດີຕ້ອນຮັບ",
      });
      navigate({
        to: "/report/overview",
      });
    },
    onError: (error) => {
      if (error.response.data.message === "EMPLOYEE_NOT_FOUND") {
        toast({
          title: "ມີບັນຫາເກີດຂື້ນ",
          description:
            "ລະຫັດຜູ້ໃຊ້ (User Code) ບໍ່ຖຶກຕ້ອງ, ກະລຸນາກວດສອບຂໍ້ມູນແລ້ວລອງອີກຄັ້ງ.",
          variant: "destructive",
        });
      } else if (error.response.data.message === "INCORRECT_PASSWORD") {
        toast({
          title: "ມີບັນຫາເກີດຂື້ນ",
          description: "ລະຫັດຜ່ານບໍ່ຖຶກຕ້ອງ,ກະລຸນາກວດສອບຂໍ້ມູນແລ້ວລອງອີກຄັ້ງ.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "ມີບັນຫາເກີດຂື້ນ",
          description: error.message || "ກະລຸນາກວດສອບຂໍ້ມູນແລ້ວລອງອີກຄັ້ງ.",
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    loginMutation.mutate(formData);
  };

  return (
    <div className="w-full lg:grid lg:min-h-[600px] lg:grid-cols-2 xl:min-h-[800px]">
      <div className="flex justify-center items-center py-12">
        <div className="mx-auto grid w-[350px] gap-6">
          <div className="grid gap-2 text-center">
            <h1 className="text-3xl font-bold">ເຂົ້າສູ່ລະບົບ</h1>
            <p className="text-balance text-muted-foreground">
              ກະລຸນາປ້ອນຂໍ້ມູນເພື່ອເຂົ້າສູ່ລະບົບ
            </p>
          </div>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="userCode">ລະຫັດຜູ້ໃຊ້ (User Code)</Label>
              <Input
                id="userCode"
                type="text"
                placeholder="SOE@xxxxxx"
                value={formData.userCode}
                onChange={(event) =>
                  setFormData({ ...formData, userCode: event.target.value })
                }
                required
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">ລະຫັດຜ່ານ</Label>
              </div>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                placeholder="********"
                value={formData.password}
                onChange={(event) =>
                  setFormData({ ...formData, password: event.target.value })
                }
              />
            </div>
            <div className="flex gap-3 items-center">
              <Checkbox
                id="show-password"
                checked={showPassword}
                onCheckedChange={(checked: boolean) => setShowPassword(checked)}
              />
              <Label htmlFor="show-password">ສະແດງລະຫັດຜ່ານ</Label>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <Spinner className="text-white" />
              ) : (
                <>
                  ເຂົ້າສູ່ລະບົບ <LogIn className="ml-2" />
                </>
              )}
            </Button>
          </form>
          <div className="flex flex-col space-y-1 text-center text-sm text-muted-foreground">
            <p>Version {version}</p>
          </div>
        </div>
      </div>
      <div className="hidden flex-col justify-center items-center bg-muted lg:flex">
        <img src="/Logo.png" alt="logo" className="w-[200px] h-[200px]" />
        <h1 className="mt-8 text-4xl font-bold">
          ລະບົບ ຄຸ້ມຄອງວິສາຫະກິດລັດລົງທຶນ
        </h1>
        <h2 className="mt-2 text-3xl">ກະຊວງການເງິນ</h2>
      </div>
    </div>
  );
}
